import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { ArrowRight, MessageSquare, Bot, Zap, Shield } from 'lucide-react';

const FloatingCard = ({ children, className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.4, 0, 0.2, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const ChatBubble = ({ message, isBot, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: isBot ? -20 : 20, scale: 0.9 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    transition={{ duration: 0.5, delay, ease: [0.4, 0, 0.2, 1] }}
    className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}
  >
    <div
      className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm font-body ${
        isBot
          ? 'bg-secondary text-secondary-foreground rounded-bl-md'
          : 'bg-primary/20 text-foreground border border-primary/20 rounded-br-md'
      }`}
    >
      {message}
    </div>
  </motion.div>
);

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Radial gradient glow behind hero */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.06] blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/[0.04] blur-[100px]" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(hsl(210 20% 95% / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(210 20% 95% / 0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="premium" className="mb-6">
                <Zap size={12} className="mr-1" />
                Now live — 7-day free trial
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-foreground mb-6"
            >
              Launch Your{' '}
              <span className="gradient-text-primary">WhatsApp AI</span>{' '}
              Agent in Minutes
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base lg:text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed font-body"
            >
              Automate customer conversations on WhatsApp with AI — no Business API needed. 
              Built for Indian SMBs who want to grow faster, spend less, and delight every customer.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-8"
            >
              <Button variant="premium" size="xl" asChild>
                <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">
                  Start 7-Day Free Trial
                  <ArrowRight size={18} />
                </a>
              </Button>
              <Button variant="glass" size="xl" asChild>
                <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">Book a Demo</a>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center gap-6 text-sm text-muted-foreground font-body"
            >
              <span className="flex items-center gap-1.5">
                <Shield size={14} className="text-primary" />
                No credit card required
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={14} className="text-primary" />
                Setup in 2 minutes
              </span>
            </motion.div>
          </div>

          {/* Right: Chat Mockup */}
          <div className="relative">
            {/* Main chat window */}
            <FloatingCard
              className="glass-card rounded-2xl p-1 glow-border-primary"
              delay={0.4}
            >
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border">
                <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                  <Bot size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground font-heading">Agentick AI</p>
                  <p className="text-xs text-primary">Online • Powered by AI</p>
                </div>
                <div className="ml-auto flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                </div>
              </div>

              {/* Chat messages */}
              <div className="p-5 space-y-1 min-h-[300px]">
                <ChatBubble
                  message="Hi! I'm looking for your pricing plans for bulk orders."
                  isBot={false}
                  delay={0.8}
                />
                <ChatBubble
                  message="Hello! Great to hear you're interested. We offer special pricing for bulk orders starting at 100+ units. Let me share the details with you."
                  isBot={true}
                  delay={1.2}
                />
                <ChatBubble
                  message="That sounds great! Can you also share delivery timelines?"
                  isBot={false}
                  delay={1.6}
                />
                <ChatBubble
                  message="Of course! Standard delivery takes 3-5 business days. For bulk orders, we offer expedited shipping at a discounted rate. Would you like a custom quote?"
                  isBot={true}
                  delay={2.0}
                />
              </div>

              {/* Chat input */}
              <div className="px-5 pb-4">
                <div className="flex items-center gap-3 bg-secondary rounded-xl px-4 py-3">
                  <MessageSquare size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground font-body">Type a message...</span>
                </div>
              </div>
            </FloatingCard>

            {/* Floating stat cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.5 }}
              className="absolute -left-6 -top-4 glass-card rounded-xl px-4 py-3 hidden lg:block animate-float-slow"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Zap size={14} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Response Time</p>
                  <p className="text-sm font-semibold text-foreground font-heading">&lt; 2 seconds</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.8 }}
              className="absolute -right-4 bottom-16 glass-card rounded-xl px-4 py-3 hidden lg:block animate-float"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center">
                  <MessageSquare size={14} className="text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Resolution Rate</p>
                  <p className="text-sm font-semibold text-foreground font-heading">95%</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
